import { Canvas } from '@/components/Canvas';
import { useAutosave } from '@/hooks/useAutosave';
import { useScenarioSession } from '@/hooks/useScenarioSession';
import { historyClient } from '@/persistence/historyClient';
import { useFunnelStore } from '@/store/useFunnelStore';

/**
 * One scenario's canvas, opened by an admin, at the /admin URL.
 *
 * `ProspectCanvas` minus the URL params and minus `useParamAnalytics`, plus a
 * bar carrying the "where am I" that the address bar deliberately does not.
 *
 * The bar is a SIBLING of the canvas, not a wrapper: `Canvas` renders its own
 * `<main>`, and nesting one inside another is invalid and breaks
 * `getByRole('main')`.
 */
interface Props {
  code: string;
  onBack: () => void;
}

export function AdminScenarioEditor({ code, onBack }: Props) {
  const { phase, retry } = useScenarioSession(code);
  const openHistory = useFunnelStore((s) => s.openHistory);

  /* Armed only once the load resolves — the same rule as the prospect canvas and
     for one more reason here: arming on the raw code would let the 750ms debounce
     beat a slow API and write the PREVIOUS scenario's params under this code. */
  useAutosave(phase === 'ready' ? code : null);

  /* Deliberately NOT useParamAnalytics. An admin dialing numbers is
     configuration, not prospect behavior, and it must not land in the funnel
     data. `track()` is already dead in admin mode; this makes it structural
     rather than dependent on a flag in another file. */

  return (
    <div className="admin-surface">
      <nav className="admin-bar" aria-label="Admin console">
        <button type="button" className="admin-bar__back" onClick={onBack}>
          All scenarios
        </button>
        <span className="admin-bar__code">
          <code>{code}</code>
        </span>
        {historyClient && phase === 'ready' && (
          <button type="button" className="admin-bar__history" onClick={openHistory}>
            History
          </button>
        )}
      </nav>

      {phase === 'loading' && (
        <main className="canvas canvas--loading">
          <p className="loading" role="status">
            Loading scenario…
          </p>
        </main>
      )}

      {phase === 'unknown-code' && (
        <main className="canvas canvas--loading">
          <div className="gate-notice" role="status">
            <h1>That code isn’t recognized</h1>
            <p>It may have been retired since this list was loaded.</p>
            <button type="button" className="gate-notice__action" onClick={onBack}>
              Back to all scenarios
            </button>
          </div>
        </main>
      )}

      {phase === 'unreachable' && (
        <main className="canvas canvas--loading">
          <div className="gate-notice" role="status">
            <h1>Can’t reach the scenario service</h1>
            <p>The code may be fine — the service just isn’t responding.</p>
            <button type="button" className="gate-notice__action" onClick={retry}>
              Try again
            </button>
            <button type="button" className="gate-notice__action" onClick={onBack}>
              Back to all scenarios
            </button>
          </div>
        </main>
      )}

      {phase === 'ready' && <Canvas scenarioId={code} />}
    </div>
  );
}
